/**
 * Shared ApexCharts theme. Hex values mirror the semantic tokens in
 * `src/index.css` because Apex needs literal colors, not CSS variables.
 */

export const palette = {
  sage: "#91B9A8",
  gold: "#D4A857",
  sky: "#7BAFD4",
  slate: "#8FA8C4",
  deep: "#3F6A58",
  rose: "#D98C8C",
};

/** Default series color order. */
export const colorway = [
  palette.sage,
  palette.gold,
  palette.sky,
  palette.slate,
  palette.deep,
  palette.rose,
];

export const heights = { sm: 180, md: 280, lg: 350 };

/** `rgba(r, g, b, alpha)` for a `#RGB` or `#RRGGBB` hex color. */
export const withAlpha = (hex, alpha) => {
  const digits = hex.replace("#", "");
  const full = digits.length === 3 ? digits.replace(/./g, "$&$&") : digits;
  const n = parseInt(full, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

const MODE_COLORS = {
  light: { fg: "#2B3674", muted: "#5E6A93", line: "#DADEEC", surface: "#FFFFFF" },
  dark: { fg: "#FFFFFF", muted: "#B0BBD5", line: "#2B3674", surface: "#111C44" },
};

/** Token hex values for the current mode: `{fg, muted, line, surface}`. */
export const getModeColors = (isDark) => (isDark ? MODE_COLORS.dark : MODE_COLORS.light);

/**
 * Base Apex options for the current color mode. Merge per-chart settings on top
 * with `mergeOptions`.
 * @param {boolean} isDark
 */
export function getBaseOptions(isDark) {
  const c = getModeColors(isDark);
  const axisLabels = { style: { colors: c.muted, fontSize: "11px", fontWeight: 500 } };

  return {
    chart: {
      fontFamily: "DM Sans, sans-serif",
      foreColor: c.muted,
      background: "transparent",
      toolbar: { show: false },
      zoom: { enabled: false },
      // `enabled` is left to Apex's default so a global `window.Apex` can switch animations off.
      animations: {
        easing: "easeinout",
        speed: 300,
        animateGradually: { enabled: false },
        dynamicAnimation: { enabled: true, speed: 250 },
      },
    },
    colors: colorway,
    grid: {
      borderColor: c.line,
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    xaxis: {
      labels: axisLabels,
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: axisLabels },
    legend: {
      position: "top",
      horizontalAlign: "left",
      fontSize: "12px",
      fontWeight: 500,
      labels: { colors: c.fg },
      markers: { width: 8, height: 8, radius: 8 },
    },
    tooltip: { theme: isDark ? "dark" : "light" },
    dataLabels: { enabled: false },
    stroke: { width: 2 },
  };
}

const isPlainObject = (value) => Object.prototype.toString.call(value) === "[object Object]";

/**
 * Deep merge of Apex option objects; `overrides` wins. Arrays replace, except an
 * array override of an object base (e.g. multiple `yaxis`) merges each item onto
 * the base so every axis keeps the theme styling. Inputs are not mutated.
 */
export function mergeOptions(base, overrides) {
  if (overrides === undefined) return base;
  if (Array.isArray(overrides)) {
    return isPlainObject(base)
      ? overrides.map((item) => (isPlainObject(item) ? mergeOptions(base, item) : item))
      : overrides;
  }
  if (!isPlainObject(base) || !isPlainObject(overrides)) return overrides;

  const result = { ...base };
  Object.keys(overrides).forEach((key) => {
    result[key] = mergeOptions(base[key], overrides[key]);
  });
  return result;
}
